import { Injectable } from '@angular/core';
import { AbstractControl, ValidatorFn } from '@angular/forms';

@Injectable({
  providedIn: 'root',
})
export class FormatUtilsService {
  constructor() {}

  formatCep(value: string) {
    if (!value) {
      return value;
    }

    value = value.replace(/\D/g, '');
    value = value.substring(0, 8);

    if (value.length > 5) {
      value = `${value.substring(0, 5)}-${value.substring(5)}`;
    }
    return value;
  }

  formatPhone(value: string) {
    if (!value) {
      return value;
    }

    value = value.replace(/\D/g, '');

    if (value.length > 10) {
      value = value.substring(0, 10);
    }

    if (value.length > 6) {
      value = `(${value.substring(0, 2)}) ${value.substring(
        2,
        6
      )}-${value.substring(6)}`;
    } else if (value.length > 2) {
      value = `(${value.substring(0, 2)}) ${value.substring(2)}`;
    } else {
      value = value;
    }

    return value;
  }

  formatMobilePhone(value: string) {
    if (!value) {
      return value;
    }

    value = value.replace(/\D/g, '');

    if (value.length > 11) {
      value = value.substring(0, 11);
    }

    if (value.length > 6) {
      value = `(${value.substring(0, 2)}) ${value.substring(
        2,
        7
      )}-${value.substring(7)}`;
    } else if (value.length > 2) {
      value = `(${value.substring(0, 2)}) ${value.substring(2)}`;
    } else {
      value = value;
    }

    return value;
  }

  formatPhoneNumber(value: string) {
    if (!value) {
      return value;
    }

    // Remove all non-digit characters
    value = value.replace(/\D/g, '');

    // Restrict the input to a maximum of 11 digits
    if (value.length > 11) {
      value = value.substring(0, 11);
    }

    // Apply house phone mask for 10 digits
    if (value.length === 10) {
      value = `(${value.substring(0, 2)}) ${value.substring(
        2,
        6
      )}-${value.substring(6)}`;
    }
    // Apply mobile phone mask for 11 digits
    else if (value.length === 11) {
      value = `(${value.substring(0, 2)}) ${value.substring(
        2,
        7
      )}-${value.substring(7)}`;
    }
    // For partial input (less than 10 or 11 digits), apply basic formatting
    else if (value.length > 6) {
      value = `(${value.substring(0, 2)}) ${value.substring(
        2,
        7
      )}-${value.substring(7)}`;
    } else if (value.length > 2) {
      value = `(${value.substring(0, 2)}) ${value.substring(2)}`;
    }

    return value;
  }

  formatCpf(value: string) {
    if (!value) {
      return '-';
    }

    value = value.replace(/\D/g, '');
    const part1 = value.substring(0, 3);
    const part2 = value.substring(3, 6);
    const part3 = value.substring(6, 9);
    const part4 = value.substring(9, 11);

    return `${part1}${part2 ? '.' + part2 : ''}${part3 ? '.' + part3 : ''}${
      part4 ? '-' + part4 : ''
    }`;
  }

  formatCnpj(value: string) {
    if (!value) {
      return '-';
    }

    value = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();

    const part1 = value.substring(0, 2);
    const part2 = value.substring(2, 5);
    const part3 = value.substring(5, 8);
    const part4 = value.substring(8, 12);
    const part5 = value.substring(12, 14);

    return `${part1}${part2 ? '.' + part2 : ''}${part3 ? '.' + part3 : ''}${
      part4 ? '/' + part4 : ''
    }${part5 ? '-' + part5 : ''}`;
  }

  validarCNH(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const cnh = control.value;

      if (!/^\d{11}$/.test(cnh)) {
        return { cnhInvalida: true };
      }

      const numbers = cnh.split('').map(Number);
      const cnhBase = numbers.slice(0, 9);
      const checkDigits = numbers.slice(9, 11);

      let sum1 = 0,
        sum2 = 0;
      let multiplier1 = 9,
        multiplier2 = 1;

      for (let i = 0; i < 9; i++) {
        sum1 += cnhBase[i] * multiplier1;
        sum2 += cnhBase[i] * multiplier2;
        multiplier1--;
        multiplier2++;
      }

      // Calculate first check digit
      let d1 = sum1 % 11;
      let d1GreaterThan9 = d1 > 9; // Rule for the second digit
      d1 = d1GreaterThan9 ? 0 : d1;

      // Calculate second check digit
      let d2 = sum2 % 11;
      if (d1GreaterThan9) {
        d2 = d2 - 2 < 0 ? d2 + 9 : d2 - 2;
      }

      d2 = d2 > 9 ? 0 : d2;

      // Check if the calculated digits match the CNH's last two digits
      if (checkDigits[0] === d1 && checkDigits[1] === d2) {
        return null;
      }

      return { cnhInvalida: true };
    };
  }

  formatMonetaryValue(value: number | string): string {
    if (!value) return 'R$0,00';

    let numericValue: number;

    if (typeof value === 'number') {
      numericValue = value;
    } else {
      const cleaned = value.replace(/\D/g, '');
      numericValue = parseInt(cleaned, 10) / 100;
    }

    if (isNaN(numericValue)) return 'R$0,00';

    let formatado = numericValue.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return `R$${formatado}`;
  }

  formatMonetaryCentsValue(value: number | string): string {
    if (value === null || value === undefined) return 'R$0,00';

    let numericValue: number;

    if (typeof value === 'string') {
      const cleanedString = value.replace(/\./g, '').replace(',', '.');
      numericValue = parseFloat(cleanedString);
    } else {
      numericValue = value;
    }

    if (isNaN(numericValue)) return 'R$0,00';

    let formatado = numericValue.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return `R$${formatado}`;
  }

  aplicarMascaraMonetaria(event: any) {
    let value = event.target.value || '';
    value = value.replace(/\D/g, '');

    if (!value) {
      event.target.value = 'R$0,00';
      return '0,00';
    }

    let numero = parseInt(value, 10);
    let formattedValue = (numero / 100).toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    formattedValue = `R$${formattedValue}`;

    event.target.value = formattedValue;
    return formattedValue;
  }

  cleanMonetaryValue(formattedValue: string): string {
    if (!formattedValue) {
      return '';
    }

    let cleanedValue = formattedValue.replace('R$', '').trim();

    cleanedValue = cleanedValue.replace(/\./g, '');
    cleanedValue = cleanedValue.replace(/,/g, '.');

    return cleanedValue;
  }

  floatMonetaryToCents(value: string): number {
    if (!value) return 0;
    const num = this.cleanMonetaryValue(value);

    return Math.round(parseFloat(num) * 100);
  }
}
